import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Route, Router, Switch, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { ToastHost } from "./components/Toast";
import { useI18n } from "./i18n";
import { IS_DEMO } from "./lib/asset";
import { Home } from "./pages/Home";
import { GamePage } from "./pages/GamePage";
import { JoinPage } from "./pages/Join";
import { RoomPage } from "./pages/Room";
import { LocalPage } from "./pages/Local";
import { NotFound } from "./pages/NotFound";

const JokerPage = lazy(() => import("./pages/Joker"));
const GuessPage = lazy(() => import("./pages/Guess"));
const AuthPage = lazy(() => import("./pages/Auth"));
const AccountPage = lazy(() => import("./pages/Account"));
const BrandPage = lazy(() => import("./pages/Brand"));
const AdminPage = lazy(() => import("./pages/Admin"));

function ScrollToTop() {
  const [loc] = useLocation();
  useEffect(() => {
    if (!location.hash || IS_DEMO) window.scrollTo({ top: 0 });
  }, [loc]);
  return null;
}

function Loading() {
  const { d } = useI18n();
  return <div className="grid min-h-[60dvh] place-items-center text-ink-3">{d.common.loading}</div>;
}

function Routes() {
  return (
    <Suspense fallback={<Loading />}>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/play/:game">{(p) => <GamePage game={p.game} />}</Route>
        <Route path="/join">{() => <JoinPage />}</Route>
        <Route path="/j/:code">{(p) => <JoinPage code={p.code} />}</Route>
        <Route path="/room/:code">{(p) => <RoomPage code={p.code.toUpperCase()} />}</Route>
        <Route path="/local/:code">{(p) => <LocalPage code={p.code} />}</Route>
        <Route path="/joker" nest>
          <JokerPage />
        </Route>
        <Route path="/guess" nest>
          <GuessPage />
        </Route>
        <Route path="/login">{() => <AuthPage mode="login" />}</Route>
        <Route path="/register">{() => <AuthPage mode="register" />}</Route>
        <Route path="/reset">{() => <AuthPage mode="reset" />}</Route>
        <Route path="/account" nest>
          <AccountPage />
        </Route>
        <Route path="/brand">{() => <BrandPage />}</Route>
        <Route path="/admin" nest>
          <AdminPage />
        </Route>
        <Route>{() => <NotFound />}</Route>
      </Switch>
    </Suspense>
  );
}

export function App(): ReactNode {
  return (
    <>
      {IS_DEMO ? (
        <Router hook={useHashLocation}>
          <Routes />
        </Router>
      ) : (
        <Router>
          <Routes />
        </Router>
      )}
      <ToastHost />
    </>
  );
}
